<template>
  <div class="step-page">
    <div class="step-header">
      <div class="step-badge">Projet : {{ projectName }}</div>
      <h1 class="step-title">Paramétrage de la simulation</h1>
    </div>

    <div class="step-content">
      <!-- Plage d'analyse — Matrice de résultats -->
      <div class="card-block" v-if="puissanceTotaleKwc > 0">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.plage ? 'false' : 'true'" @click="replie.plage = !replie.plage">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.plage ? '▸' : '▾' }}</span>Plage d'analyse — Matrice de résultats
          <span v-if="replie.plage" class="section-repliable__resume">{{ resumePlage }}</span>
          <span v-if="nbModifies.plage" class="badge-modifies">{{ libelleModifies(nbModifies.plage) }}</span>
        </button>
        <div v-show="!replie.plage">
        <p class="card-subtitle">
          5 puissances PV × 5 capacités batterie seront simulées dans ces plages.
          Chaque combinaison est calculée indépendamment et affichée dans la matrice de scénarios.
        </p>
        <div class="range-group">
          <div class="range-group__label">Puissance PV (kWc)</div>
          <div class="form-grid form-grid--4">
            <div class="form-group">
              <label class="form-label">Min</label>
              <input v-model.number="form.pvRangeMin" type="number" :min="pvMinimum" step="1" class="form-input" :placeholder="'Ex : ' + pvMinimum" />
              <defaut-champ :valeur="form.pvRangeMin" :defaut="defauts.pvRangeMin" unite="kWc" @retablir="retablir('pvRangeMin')" />
            </div>
            <div class="form-group">
              <label class="form-label">Max</label>
              <input
                v-model.number="form.pvRangeMax"
                type="number"
                :min="form.pvRangeMin || 1"
                step="1"
                class="form-input"
                :placeholder="'Ex : ' + Math.round(puissanceTotaleKwc)"
              />
              <defaut-champ :valeur="form.pvRangeMax" :defaut="defauts.pvRangeMax" unite="kWc" @retablir="retablir('pvRangeMax')" />
            </div>
          </div>
          <p v-if="plagePvIncoherente && !toiturePropTropPetite" class="range-error">Le minimum doit être inférieur ou égal au maximum.</p>
          <!-- professionnel : pas d'étude sous 9 kWc (règle du 27/10/2026) -->
          <p v-if="toiturePropTropPetite" class="range-error plage-pro">Une étude professionnelle commence à {{ pvMinimum }} kWc : la toiture permet au plus {{ nombreFr(puissanceTotaleKwc, 2) }} kWc (Step 2).</p>
          <p v-else-if="plagePvSousMinimumPro" class="range-error plage-pro">Une étude professionnelle commence à {{ pvMinimum }} kWc : minimum à porter à {{ pvMinimum }} kWc au moins.</p>
        </div>
        <div class="range-group">
          <div class="range-group__label">Capacité batterie (kWh)</div>
          <div class="form-grid form-grid--4">
            <div class="form-group">
              <label class="form-label">Min</label>
              <input v-model.number="form.batRangeMin" type="number" min="0" step="1" class="form-input" placeholder="Ex : 5" />
              <defaut-champ :valeur="form.batRangeMin" :defaut="defauts.batRangeMin" unite="kWh" @retablir="retablir('batRangeMin')" />
            </div>
            <div class="form-group">
              <label class="form-label">Max</label>
              <input
                v-model.number="form.batRangeMax"
                type="number"
                :min="(form.batRangeMin || 0) + 1"
                step="1"
                class="form-input"
                placeholder="Ex : 25"
              />
              <defaut-champ :valeur="form.batRangeMax" :defaut="defauts.batRangeMax" unite="kWh" @retablir="retablir('batRangeMax')" />
            </div>
          </div>
          <p v-if="plageBatIncoherente" class="range-error">Le minimum doit être inférieur ou égal au maximum.</p>
          <p class="field-note">* La configuration sans batterie (0 kWh) est toujours incluse dans le calcul.</p>
        </div>
      </div>
      </div>

      <!-- Paramètres de calcul -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.calcul ? 'false' : 'true'" @click="replie.calcul = !replie.calcul">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.calcul ? '▸' : '▾' }}</span>Paramètres de calcul
          <span v-if="replie.calcul" class="section-repliable__resume">{{ resumeCalcul }}</span>
          <span v-if="nbModifies.calcul" class="badge-modifies">{{ libelleModifies(nbModifies.calcul) }}</span>
        </button>
        <div v-show="!replie.calcul">
        <div class="form-grid form-grid--3">
          <div class="form-group">
            <label class="form-label">Inflation de l'électricité (%/an)</label>
            <input v-model.number="form.inflationElec" type="number" min="0" max="50" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.inflationElec" :defaut="defauts.inflationElec" unite="%/an" @retablir="retablir('inflationElec')" />
          </div>
          <div class="form-group">
            <label class="form-label">Dégradation panneaux (%/an)</label>
            <input v-model.number="form.degradationPanneaux" type="number" min="0" max="5" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.degradationPanneaux" :defaut="defauts.degradationPanneaux" unite="%/an" @retablir="retablir('degradationPanneaux')" />
          </div>
          <div class="form-group">
            <label class="form-label">Dégradation batterie (%/an)</label>
            <input v-model.number="form.degradationBatterie" type="number" min="0" max="20" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.degradationBatterie" :defaut="defauts.degradationBatterie" unite="%/an" @retablir="retablir('degradationBatterie')" />
          </div>
          <div class="form-group">
            <label class="form-label" title="Rendement round-trip de la batterie (perte électrochimique à la charge). Valeur constructeur par défaut : 90 %.">Rendement batterie (%)</label>
            <input v-model.number="form.rendementBatterie" type="number" min="50" max="100" step="1" class="form-input" />
            <defaut-champ :valeur="form.rendementBatterie" :defaut="defauts.rendementBatterie" unite="%" @retablir="retablir('rendementBatterie')" />
          </div>
          <div class="form-group">
            <label class="form-label" title="Rendement de conversion CC/AC de l'onduleur hybride, appliqué uniquement à la décharge batterie (la charge PV→batterie reste en CC, sans perte de conversion). Valeur mesurée sur étude PV*SOL : ~98 %.">Rendement conversion CC/AC décharge (%)</label>
            <input v-model.number="form.rendementConversionBatterie" type="number" min="80" max="100" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.rendementConversionBatterie" :defaut="defauts.rendementConversionBatterie" unite="%" @retablir="retablir('rendementConversionBatterie')" />
          </div>
          <div class="form-group">
            <label class="form-label" title="Part de la capacité nominale réellement utilisée (profondeur de décharge). Valeur courante : 90 %.">Capacité de batterie utilisée (%)</label>
            <input v-model.number="form.capaciteUtilisableBatterie" type="number" min="50" max="100" step="1" class="form-input" />
            <defaut-champ :valeur="form.capaciteUtilisableBatterie" :defaut="defauts.capaciteUtilisableBatterie" unite="%" @retablir="retablir('capaciteUtilisableBatterie')" />
          </div>
        </div>
      </div>
      </div>

      <!-- Hypothèses de coûts d'installation : grilles des études d'avant le chiffrage au matériel (03/11/2026) -->
      <div v-if="!form.chiffrageMateriel" class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.couts ? 'false' : 'true'" @click="replie.couts = !replie.couts">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.couts ? '▸' : '▾' }}</span>Hypothèses de coûts d'installation
          <span v-if="replie.couts" class="section-repliable__resume">Grilles de prix PV et batterie</span>
          <span v-if="nbModifies.couts" class="badge-modifies">{{ libelleModifies(nbModifies.couts) }}</span>
        </button>
        <div v-show="!replie.couts">
        <p class="card-subtitle">
          Coûts d'installation utilisés pour estimer l'investissement et calculer la rentabilité du projet.
        </p>
        <p v-if="form.chiffrageMateriel" class="grilles-inutilisees">
          Grilles non utilisées : le chiffrage au matériel est activé (« Paramétrage des coûts »).
        </p>

        <div class="two-tables" :class="{ 'two-tables--inutilisees': form.chiffrageMateriel }">
          <!-- Coût installation PV -->
          <div class="table-block">
            <div class="table-block__title">Coût Installation PV HT (€/Wc)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Puissance</th>
                  <th>Prix HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutInstallationPV" :key="'pv-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input
                        :value="(row.prix / 1000).toFixed(3)"
                        type="number" min="0" step="0.001"
                        class="form-input form-input--sm"
                        @input="row.prix = Math.round(parseFloat($event.target.value || 0) * 1000)"
                      />
                      <span class="unit">€/Wc</span>
                    </div>
                    <defaut-champ :valeur="row.prix / 1000" :defaut="prixDefaut('coutInstallationPV', idx, 1000)" unite="€/Wc" :decimales="3" @retablir="retablirTranche('coutInstallationPV', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Coût installation batterie -->
          <div class="table-block">
            <div class="table-block__title">Coût Installation Batterie HT (€/kWh)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Capacité</th>
                  <th>Prix HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutInstallationBatterie" :key="'bat-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input v-model.number="row.prix" type="number" min="0" step="10" class="form-input form-input--sm" />
                      <span class="unit">€/kWh</span>
                    </div>
                    <defaut-champ :valeur="row.prix" :defaut="prixDefaut('coutInstallationBatterie', idx)" unite="€/kWh" @retablir="retablirTranche('coutInstallationBatterie', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
      </div>

      <!-- Hypothèses de coûts du câble onduleur ↔ TGBT (19/10/2026) : prix du fichier du BE, modifiables, repliées par défaut -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.cable ? 'false' : 'true'" @click="replie.cable = !replie.cable">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.cable ? '▸' : '▾' }}</span>Hypothèses de coûts du câble onduleur ↔ TGBT
          <span v-if="replie.cable" class="section-repliable__resume">Prix au mètre selon la section et raccords</span>
          <span v-if="nbModifies.cable" class="badge-modifies">{{ libelleModifies(nbModifies.cable) }}</span>
        </button>
        <div v-show="!replie.cable">
        <p v-if="cablesCatalogue" class="grilles-inutilisees">
          Grille non utilisée : câbles du catalogue du matériel (professionnel chiffré au matériel).
        </p>
        <p class="card-subtitle">Case vide : prix non disponible.</p>
        <div class="table-block table-block--cable">
          <div class="table-block__title">Prix du câble HT (€/m)</div>
          <table class="params-table params-table--cable">
            <thead>
              <tr>
                <th>Section</th>
                <th>Alu triphasé</th>
                <th>Alu monophasé</th>
                <th>Cuivre triphasé</th>
                <th>Cuivre monophasé</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in form.prixCable" :key="'cable-' + idx">
                <td class="label-cell">{{ sectionTexte(row.section) }} mm²</td>
                <td v-for="col in COLONNES_CABLE" :key="col">
                  <input :value="row[col]" type="number" min="0" step="0.01" class="form-input form-input--sm"
                    :aria-label="'Prix ' + col + ' ' + sectionTexte(row.section) + ' mm²'"
                    @input="row[col] = $event.target.value === '' ? null : Math.max(0, parseFloat($event.target.value) || 0)" />
                  <defaut-champ :valeur="row[col]" :defaut="prixCableDefaut(idx, col)" unite="€/m" @retablir="row[col] = prixCableDefaut(idx, col)" />
                </td>
              </tr>
            </tbody>
          </table>
          <table class="params-table params-table--raccords">
            <thead>
              <tr>
                <th>Raccord (câble aluminium jusqu'à 36 kVA)</th>
                <th>Prix HT pièce</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in form.prixRaccordsAlu" :key="'raccord-' + idx">
                <td class="label-cell">{{ row.label }} ({{ sectionTexte(row.sectionMin) }} à {{ sectionTexte(row.sectionMax) }} mm²)</td>
                <td>
                  <div class="input-unit">
                    <input v-model.number="row.prix" type="number" min="0" step="1" class="form-input form-input--sm" />
                    <span class="unit">€</span>
                  </div>
                  <defaut-champ :valeur="row.prix" :defaut="prixDefaut('prixRaccordsAlu', idx)" unite="€" @retablir="retablirTranche('prixRaccordsAlu', idx)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>

      <!-- Hypothèses de coûts d'exploitation (OPEX) : grilles, repliées par défaut (13/10/2026) -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.opex ? 'false' : 'true'" @click="replie.opex = !replie.opex">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.opex ? '▸' : '▾' }}</span>Hypothèses de coûts d'exploitation (OPEX)
          <span v-if="replie.opex" class="section-repliable__resume">Grilles et hausses de la maintenance et de l'assurance</span>
          <span v-if="nbModifies.opex" class="badge-modifies">{{ libelleModifies(nbModifies.opex) }}</span>
        </button>
        <div v-show="!replie.opex">
        <p class="card-subtitle">Coûts de maintenance et d'assurance annuels HT selon la puissance installée.</p>
        <!-- hausses dans les hypothèses ; interrupteurs dans « Paramétrage des coûts » (13/10/2026) -->
        <div class="opex-phrases">
          <p class="opex-phrase">
            <label for="hausse-maintenance-pct">Hausse de la maintenance :</label>
            <input id="hausse-maintenance-pct" v-model.number="form.hausseMaintenancePct" type="number" min="0" max="50" step="0.1" class="form-input trou" aria-label="Hausse de la maintenance (%)" />
            <span>% tous les</span>
            <input id="hausse-maintenance-periode" v-model.number="form.hausseMaintenancePeriode" type="number" min="1" max="20" step="1" class="form-input trou" aria-label="Période de la hausse (ans)" />
            <span>ans.</span>
            <defaut-champ :valeur="form.hausseMaintenancePct" :defaut="defauts.hausseMaintenancePct" unite="%" @retablir="retablir('hausseMaintenancePct')" />
            <defaut-champ :valeur="form.hausseMaintenancePeriode" :defaut="defauts.hausseMaintenancePeriode" unite="ans" @retablir="retablir('hausseMaintenancePeriode')" />
          </p>
          <p class="opex-phrase">
            <label for="inflation-assurance">Inflation de l'assurance :</label>
            <input id="inflation-assurance" v-model.number="form.inflationAssurance" type="number" min="0" max="20" step="0.1" class="form-input trou" aria-label="Inflation de l'assurance (%/an)" />
            <span>%/an.</span>
            <defaut-champ :valeur="form.inflationAssurance" :defaut="defauts.inflationAssurance" unite="%/an" @retablir="retablir('inflationAssurance')" />
          </p>
        </div>
        <div class="two-tables">
          <div class="table-block">
            <div class="table-block__title">Maintenance annuelle (forfait HT / an)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Tranche de puissance</th>
                  <th>Forfait annuel HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutMaintenance" :key="'maint-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input v-model.number="row.prix" type="number" min="0" step="10" class="form-input form-input--sm" />
                      <span class="unit">€/an</span>
                    </div>
                    <defaut-champ :valeur="row.prix" :defaut="prixDefaut('coutMaintenance', idx)" unite="€/an" @retablir="retablirTranche('coutMaintenance', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-block">
            <div class="table-block__title">Assurance annuelle (forfait HT / an)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Tranche de puissance</th>
                  <th>Forfait annuel HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutAssurance" :key="'assur-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input v-model.number="row.prix" type="number" min="0" step="5" class="form-input form-input--sm" />
                      <span class="unit">€/an</span>
                    </div>
                    <defaut-champ :valeur="row.prix" :defaut="prixDefaut('coutAssurance', idx)" unite="€/an" @retablir="retablirTranche('coutAssurance', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>

      <!-- Paramétrage des coûts (13/10/2026) : coûts d'installation, OPEX, financement et vente de surplus — déroulé -->
      <div class="card-block parametrage-couts">
        <div class="card-block__title">Paramétrage des coûts</div>

        <div class="sous-titre">Coûts d'installation</div>
        <!-- chiffrage au matériel (03/11/2026 : toujours, plus d'interrupteur) ; une étude d'avant garde ses grilles de prix -->
        <div v-if="!form.chiffrageMateriel" class="cout-ligne grilles-ancienne-etude">
          <span class="opex-off">Étude chiffrée avec les grilles de prix (enregistrée avant le chiffrage au matériel).</span>
          <button type="button" class="btn-passer-materiel" @click="form.chiffrageMateriel = true">Chiffrer au matériel</button>
        </div>
        <div v-if="form.chiffrageMateriel" class="materiel-choix">
          <p v-if="catalogueErreur" class="materiel-erreur">{{ catalogueErreur }}</p>
          <p v-else-if="!catalogueClient" class="materiel-note">Chargement du catalogue…</p>
          <template v-else>
            <div class="sous-sous-titre">Matériel</div>
            <!-- panneau et systèmes d'intégration choisis en Step 2 -->
            <div class="cout-ligne">
              <span class="cout-ligne__libelle">Panneaux</span>
              <span class="materiel-panneau">{{ panneauTexte }}</span>
            </div>
            <div class="cout-ligne">
              <span class="cout-ligne__libelle">Systèmes d'intégration</span>
              <span class="materiel-panneau materiel-integrations">{{ integrationsTexte }}</span>
            </div>
            <!-- professionnel : fabricant des onduleurs réseau, choisi par le commercial (03/11/2026) ; RETIX choisit les modèles -->
            <div v-if="!estParticulier" class="cout-ligne">
              <label class="cout-ligne__libelle" for="materiel-marque">Onduleurs réseau</label>
              <select id="materiel-marque" v-model="form.materiel.marqueOnduleur" class="form-input form-input--sm materiel-select" :class="{ 'champ-a-choisir': !form.materiel.marqueOnduleur }" @change="form.materiel.gammeOnduleur = ''">
                <option value="" disabled>— Choisir un fabricant —</option>
                <option v-for="g in catalogueClient.onduleursReseau || []" :key="g.marque" :value="g.marque">{{ g.marque }} : {{ plageKva(g) }}</option>
              </select>
            </div>
            <div class="cout-ligne">
              <label class="cout-ligne__libelle" for="materiel-batterie">Onduleurs hybrides et batteries</label>
              <select v-if="!estParticulier" id="materiel-batterie" v-model="systemeBatterie" class="form-input form-input--sm materiel-select" :class="{ 'champ-a-choisir': batterieAChoisir }">
                <option value="" disabled>— Choisir une gamme —</option>
                <option v-for="o in catalogueClient.systemesBatterie || []" :key="o.valeur" :value="o.valeur">{{ libelleSystemeBatterie(o) }}</option>
              </select>
              <span v-else class="materiel-systeme">{{ (catalogueClient.systemesBatterie || []).map(libelleSystemeBatterie).join(' ; ') }}</span>
            </div>

            <div class="sous-sous-titre">Options</div>
            <label v-if="catalogueClient.backup" class="cout-ligne materiel-case">
              <input id="materiel-backup" type="checkbox" v-model="form.materiel.backup" />
              {{ estParticulier ? 'Backup (secours en cas de coupure, avec batterie)' : 'Backup partiel (secours en cas de coupure, avec batterie)' }}
            </label>
            <p v-if="!estParticulier && catalogueClient.backup && form.materiel.backup" class="materiel-note materiel-note--backup">
              Coffret, protection et câble du backup, de la longueur du câble onduleur ↔ TGBT{{ form.longueurCable ? ' (' + nombreFr(form.longueurCable, 0) + ' m)' : ' (longueur non saisie : pas de câble)' }}.
            </p>
            <label v-if="estParticulier && catalogueClient.optimiseur" class="cout-ligne materiel-case">
              <input id="materiel-optimiseurs" type="checkbox" v-model="form.materiel.optimiseurs" />
              Optimiseurs ({{ catalogueClient.optimiseur }})
            </label>
            <label v-if="!estParticulier && catalogueClient.shelter" class="cout-ligne materiel-case">
              <input id="materiel-shelter" type="checkbox" v-model="form.materiel.shelter" />
              Shelter (prix selon le nombre d'onduleurs)
            </label>
            <div class="cout-ligne">
              <label class="cout-ligne__libelle" for="materiel-garantie">Garantie des onduleurs</label>
              <select id="materiel-garantie" v-model.number="form.materiel.garantieOnduleurAns" class="form-input form-input--sm materiel-select materiel-select--court">
                <option v-for="a in (catalogueClient.garantiesOnduleurs || [5, 10, 15, 20])" :key="a" :value="a">{{ a === 5 ? '5 ans (de base)' : a + ' ans' }}</option>
              </select>
            </div>
            <!-- ombrières (pro) : posées par nous ou sous-traitées ; structure primaire toujours en prestation externe -->
            <div v-if="!estParticulier && aDesOmbrieres" class="cout-ligne">
              <span class="cout-ligne__libelle">Pose des ombrières</span>
              <div class="choix-ombrieres">
                <label class="materiel-case"><input id="ombrieres-nous" type="radio" :value="true" v-model="form.materiel.ombriereParNous" /> Posées par nous</label>
                <label class="materiel-case"><input id="ombrieres-sous-traitees" type="radio" :value="false" v-model="form.materiel.ombriereParNous" /> Sous-traitées (grille de sous-traitance)</label>
              </div>
            </div>
            <template v-if="!estParticulier && catalogueClient.bacAcier">
              <label class="cout-ligne materiel-case">
                <input id="materiel-bac-acier" type="checkbox" v-model="form.materiel.bacAcier" />
                Bac acier : fourniture et pose (prestation externe)
              </label>
              <div v-if="form.materiel.bacAcier" class="cout-ligne">
                <label class="cout-ligne__libelle" for="materiel-bac-acier-m2">Surface de bac acier</label>
                <div class="input-unit">
                  <input id="materiel-bac-acier-m2" v-model.number="form.materiel.bacAcierM2" type="number" min="0" step="1" class="form-input form-input--sm" />
                  <span class="unit">m²</span>
                </div>
              </div>
            </template>
            <!-- bornes de charge (03/11/2026) : modèle et nombre, CAPEX à part -->
            <div class="cout-ligne cout-ligne--haut">
              <span class="cout-ligne__libelle">Bornes de charge</span>
              <div class="bornes">
                <p v-if="!bornesCatalogue.length" class="materiel-note">Aucune borne de charge dans le catalogue pour l'instant.</p>
                <div v-for="(b, i) in form.materiel.bornes" :key="i" class="borne-ligne">
                  <select v-model="b.ref" class="form-input form-input--sm materiel-select borne-modele">
                    <option value="" disabled>— Modèle —</option>
                    <option v-for="x in bornesCatalogue" :key="x.ref" :value="x.ref" :disabled="!x.disponible">{{ x.libelle }}{{ x.disponible ? '' : ' (non disponible)' }}</option>
                  </select>
                  <input v-model.number="b.nombre" type="number" min="1" step="1" class="form-input form-input--sm borne-nombre" aria-label="Nombre de bornes" />
                  <button type="button" class="btn-retirer-borne" title="Retirer cette borne" @click="form.materiel.bornes.splice(i, 1)">✕</button>
                </div>
                <button v-if="bornesCatalogue.length" type="button" class="btn-ajouter-borne" @click="form.materiel.bornes.push({ ref: '', nombre: 1 })">+ Ajouter une borne</button>
              </div>
            </div>

            <div class="sous-sous-titre">Marges <span class="sous-titre-note">(minimum du catalogue ; modifiables aussi dans le rapport)</span></div>
            <div v-for="m in margesAffichees" :key="m.cle" class="cout-ligne">
              <label class="cout-ligne__libelle" :for="'marge-' + m.cle">{{ m.libelle }}</label>
              <div class="input-unit">
                <input :id="'marge-' + m.cle" :value="form.materiel[m.cle]" type="number" step="1" min="0" max="99" class="form-input form-input--sm"
                  :placeholder="m.min != null ? nombreFr(m.min, 0) : ''" @input="changerMarge(m.cle, $event.target.value)" />
                <span class="unit">%</span>
              </div>
              <defaut-champ :valeur="form.materiel[m.cle] != null ? form.materiel[m.cle] : m.min" :defaut="m.min" unite="%" @retablir="changerMarge(m.cle, '')" />
              <span v-if="m.sousMinimum" class="materiel-erreur marge-sous-minimum">Sous la marge minimale ({{ nombreFr(m.min, 0) }} %)</span>
            </div>
            <div class="cout-ligne">
              <label class="cout-ligne__libelle" for="materiel-ajustement">Ajustement du prix</label>
              <div class="input-unit">
                <input id="materiel-ajustement" v-model.number="form.materiel.ajustementPct" type="number" step="1" class="form-input form-input--sm" />
                <span class="unit">%</span>
              </div>
            </div>
            <!-- ratios panneaux / onduleur (29/10/2026) : défaut du catalogue, au-delà de la limite valider avec le BE -->
            <template v-if="catalogue && catalogue.ratios">
              <div v-if="!estParticulier" class="cout-ligne">
                <label class="cout-ligne__libelle" for="materiel-ratio-reseau">Ratio panneaux / onduleur réseau</label>
                <div class="input-unit">
                  <input id="materiel-ratio-reseau" v-model.number="ratioReseau" type="number" step="0.05" min="0" class="form-input form-input--sm" :placeholder="nombreFr(catalogue.ratios.reseau, 2)" />
                </div>
                <defaut-champ :valeur="ratioReseauEffectif" :defaut="catalogue.ratios.reseau" @retablir="ratioReseau = null" />
              </div>
              <p v-if="!estParticulier && ratioReseauEffectif > catalogue.ratios.reseauMaxBe" class="materiel-erreur materiel-ratio-be">
                Au-delà de {{ nombreFr(catalogue.ratios.reseauMaxBe, 2) }} : validez cette valeur avec le bureau d'études.
              </p>
              <div class="cout-ligne">
                <label class="cout-ligne__libelle" for="materiel-ratio-hybride">Ratio panneaux / onduleur hybride</label>
                <div class="input-unit">
                  <input id="materiel-ratio-hybride" v-model.number="ratioHybride" type="number" step="0.05" min="0" class="form-input form-input--sm" :placeholder="nombreFr(catalogue.ratios.hybride, 2)" />
                </div>
                <defaut-champ :valeur="ratioHybrideEffectif" :defaut="catalogue.ratios.hybride" @retablir="ratioHybride = null" />
              </div>
              <p v-if="ratioHybrideEffectif > catalogue.ratios.hybrideMaxBe" class="materiel-erreur materiel-ratio-be">
                Au-delà de {{ nombreFr(catalogue.ratios.hybrideMaxBe, 2) }} : validez cette valeur avec le bureau d'études.
              </p>
            </template>
            <p class="materiel-note">
              Pour chaque scénario : modèles d'onduleurs choisis par RETIX selon la puissance, parmi les fabricants choisis
              (avec une batterie : onduleurs hybrides ou kit de la gamme choisie), main d'œuvre et engins selon la puissance et la typologie.
              La production ne dépasse pas la puissance des onduleurs ; la batterie charge et décharge au plus à ses puissances de charge et de décharge.
              Batteries proposées : {{ batteriesTexte }}.
              <template v-if="estParticulier"> Particulier : jusqu'à {{ nombreFr(kwcMaxParticulier, 0) }} kWc de panneaux.</template>
              Le détail du coût par section est dans le rapport.
            </p>
          </template>
        </div>
        <!-- un coût par ligne, libellé centré sur la case (13/10/2026) -->
        <!-- ordre du 20/10/2026 : câble, datalogger (pro), raccordement, travaux -->
        <!-- câble onduleur ↔ TGBT (19/10/2026) : section et prix calculés pour chaque scénario (méthode du BE) -->
        <div class="cout-ligne enedis-field cable-field">
          <label class="cout-ligne__libelle" for="longueur-cable">Longueur du câble onduleur ↔ TGBT</label>
          <div class="input-unit">
            <input id="longueur-cable" :value="form.longueurCable" type="number" min="0" step="1" class="form-input form-input--sm" placeholder="non chiffré"
              @input="form.longueurCable = $event.target.value === '' ? null : Math.max(0, parseFloat($event.target.value) || 0)" />
            <span class="unit">m</span>
          </div>
        </div>
        <!-- établissement recevant du public (pro) : câble majoré de 30 % (27/10/2026) -->
        <div v-if="clientType !== 'Particulier'" class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input id="erp" type="checkbox" v-model="form.erp" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Établissement recevant du public (ERP)</span>
          </label>
          <span v-if="form.erp" class="opex-off">{{ cablesCatalogue ? 'Câbles « ERP » du catalogue.' : 'Prix du câble majoré de 30 %.' }}</span>
        </div>
        <!-- datalogger : professionnels seulement (12/10/2026), prix selon la puissance d'abonnement (13/10/2026) -->
        <div v-if="clientType !== 'Particulier'" class="cout-ligne enedis-field">
          <label class="cout-ligne__libelle" for="datalogger">Datalogger (HT)</label>
          <div class="input-unit">
            <!-- modifiable (13/10/2026) : vide = prix selon la puissance d'abonnement -->
            <input id="datalogger" :value="form.prixDatalogger != null ? form.prixDatalogger : prixDataloggerDefaut" type="number" min="0" step="100"
              class="form-input form-input--sm" @input="form.prixDatalogger = $event.target.value === '' ? null : Math.max(0, parseFloat($event.target.value) || 0)" />
            <span class="unit">€</span>
          </div>
          <defaut-champ :valeur="form.prixDatalogger != null ? form.prixDatalogger : prixDataloggerDefaut" :defaut="prixDataloggerDefaut" unite="€" @retablir="form.prixDatalogger = null" />
        </div>
        <div class="cout-ligne enedis-field">
          <!-- raccordement ENEDIS : sans TVA (13/10/2026) -->
          <label class="cout-ligne__libelle" for="cout-raccordement">Coût de raccordement ENEDIS</label>
          <div class="input-unit">
            <input id="cout-raccordement" v-model.number="form.coutRaccordementEnedis" type="number" min="0" step="1" class="form-input form-input--sm" />
            <span class="unit">€</span>
          </div>
        </div>
        <div class="cout-ligne enedis-field travaux-field">
          <label class="cout-ligne__libelle" for="travaux-montant">Travaux supplémentaires (HT)</label>
          <div class="input-unit">
            <input id="travaux-montant" v-model.number="form.travauxSupplementaires" type="number" min="0" step="100" class="form-input form-input--sm" placeholder="0" />
            <span class="unit">€</span>
          </div>
          <input v-model="form.travauxSupplementairesLibelle" type="text" class="form-input form-input--sm travaux-libelle"
            placeholder="Nature des travaux (ex. renfort de charpente)" aria-label="Nature des travaux supplémentaires" />
        </div>
        <div class="sous-titre">Coûts OPEX</div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="form.avecMaintenance" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Maintenance</span>
          </label>
          <span v-if="!form.avecMaintenance" class="opex-off">Non comptée dans la rentabilité.</span>
          <defaut-champ :valeur="form.avecMaintenance" :defaut="defauts.avecMaintenance" @retablir="retablir('avecMaintenance')" />
        </div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="form.avecAssurance" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Assurance</span>
          </label>
          <span v-if="!form.avecAssurance" class="opex-off">Non comptée dans la rentabilité.</span>
          <defaut-champ :valeur="form.avecAssurance" :defaut="defauts.avecAssurance" @retablir="retablir('avecAssurance')" />
        </div>

        <div class="sous-titre">Financement</div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="form.avecFinancement" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Financement</span>
          </label>
          <span v-if="!form.avecFinancement" class="opex-off">Sans emprunt.</span>
          <defaut-champ :valeur="form.avecFinancement" :defaut="defauts.avecFinancement" @retablir="retablir('avecFinancement')" />
        </div>
        <div v-if="form.avecFinancement" class="financement-champs">
          <div class="cout-ligne">
            <label class="cout-ligne__libelle" for="apport-pct">Apport (% du montant total)</label>
            <div class="input-unit">
              <input id="apport-pct" v-model.number="form.apportPct" type="number" min="0" max="100" step="1" class="form-input form-input--sm" />
              <span class="unit">%</span>
            </div>
            <defaut-champ :valeur="form.apportPct" :defaut="defauts.apportPct" unite="%" @retablir="retablir('apportPct')" />
          </div>
          <div class="cout-ligne">
            <label class="cout-ligne__libelle" for="taux-emprunt">Taux annuel</label>
            <div class="input-unit">
              <input id="taux-emprunt" v-model.number="form.tauxEmprunt" type="number" min="0" max="30" step="0.1" class="form-input form-input--sm" />
              <span class="unit">%</span>
            </div>
            <defaut-champ :valeur="form.tauxEmprunt" :defaut="defauts.tauxEmprunt" unite="%" @retablir="retablir('tauxEmprunt')" />
          </div>
          <!-- durée au plus court (26/10/2026) : calculée pour chaque scénario ; la durée saisie ne sert que si aucune ne convient
               (interrupteur à droite de la durée, demande du 26/10/2026) -->
          <div class="cout-ligne">
            <label class="cout-ligne__libelle" for="duree-emprunt">{{ form.dureeEmpruntAuPlusCourt ? "Durée si non rentable sur 30 ans" : "Durée de l'emprunt" }}</label>
            <div class="input-unit">
              <input id="duree-emprunt" v-model.number="form.dureeEmprunt" type="number" min="1" max="30" step="1" class="form-input form-input--sm" />
              <span class="unit">ans</span>
            </div>
            <defaut-champ :valeur="form.dureeEmprunt" :defaut="defauts.dureeEmprunt" unite="ans" @retablir="retablir('dureeEmprunt')" />
            <label class="toggle-row" title="Pour chaque scénario, l'emprunt se termine l'année où le projet devient rentable. La durée saisie ne sert que si le projet n'est pas rentable sur 30 ans.">
              <span class="toggle-switch">
                <input id="duree-au-plus-court" type="checkbox" v-model="form.dureeEmpruntAuPlusCourt" />
                <span class="toggle-slider"></span>
              </span>
              <span class="toggle-row__label">Au plus court</span>
            </label>
            <span v-if="form.dureeEmpruntAuPlusCourt" class="opex-off">Fin de l'emprunt l'année où le projet devient rentable.</span>
            <defaut-champ :valeur="form.dureeEmpruntAuPlusCourt" :defaut="defauts.dureeEmpruntAuPlusCourt" @retablir="retablir('dureeEmpruntAuPlusCourt')" />
          </div>
        </div>

        <!-- vente de surplus en dernier, désactivée par défaut = zéro-injection (13/10/2026) -->
        <div class="sous-titre">Valorisation de l'énergie</div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="venteSurplus" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Vente de surplus</span>
          </label>
          <span v-if="!venteSurplus" class="opex-off">Zéro-injection : le surplus n'est pas valorisé (onduleur bridé).</span>
          <defaut-champ :valeur="venteSurplus" :defaut="!defauts.zeroInjection" @retablir="retablir('zeroInjection')" />
        </div>
        <div v-if="venteSurplus" class="cout-ligne">
          <label class="cout-ligne__libelle" for="rachat-surplus">Rachat du surplus</label>
          <div class="input-unit">
            <input id="rachat-surplus" v-model.number="form.rachatSurplusUnitaire" type="number" min="0" step="0.001" class="form-input form-input--sm" />
            <span class="unit">€/kWh</span>
          </div>
          <defaut-champ :valeur="form.rachatSurplusUnitaire" :defaut="defauts.rachatSurplusUnitaire" unite="€/kWh" @retablir="retablir('rachatSurplusUnitaire')" />
        </div>
      </div>

      <!-- Info -->
      <div class="info-banner">
        <span class="info-banner__icon">ℹ️</span>
        <span>
          <strong>Rassurez-vous !</strong> Vous pourrez toujours adapter vos données et vos paramètres même après la génération du rapport.
        </span>
      </div>
    </div>

    <div class="step-footer">
      <button class="btn-back" @click="$emit('back')">Retour</button>
      <span v-if="plagesIncoherentes" class="range-error footer-erreur">Plage d'analyse à corriger (premier cadre).</span>
      <span v-else-if="materielIncomplet" class="range-error footer-erreur materiel-incomplet">{{ materielIncomplet }}</span>
      <button class="btn-next" :disabled="plagesIncoherentes || !!materielIncomplet" @click="submit">Générer mon rapport →</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { buildInitialState, KWC_MIN_PRO, DEFAULT_MATERIEL, TYPOLOGIES } from '@/store/modules/wizard.module'
import { nombreFr } from '@/utils/format'
import DefautChamp, { estModifie } from '@/components/DefautChamp.vue'
import { catalogueEtude, chargerCatalogueEtude, defautsEtude, margesMinimales, SECTIONS_MARGE } from '@/utils/defautsEtude'

// réglages de chaque section repliable, pour compter ceux qui s'écartent des valeurs par défaut (19/10/2026)
// colonnes de la grille du câble : aluminium / cuivre, 4 conducteurs (triphasé) / 2 (monophasé)
const COLONNES_CABLE = ['alu4', 'alu2', 'cuivre4', 'cuivre2']

const SECTIONS = {
  plage: ['pvRangeMin', 'pvRangeMax', 'batRangeMin', 'batRangeMax'],
  calcul: ['inflationElec', 'degradationPanneaux', 'degradationBatterie', 'rendementBatterie', 'rendementConversionBatterie', 'capaciteUtilisableBatterie'],
  couts: ['coutInstallationPV', 'coutInstallationBatterie'],
  cable: ['prixCable', 'prixRaccordsAlu'],
  opex: ['hausseMaintenancePct', 'hausseMaintenancePeriode', 'inflationAssurance', 'coutMaintenance', 'coutAssurance'],
}

export default {
  name: 'Step4Parametres',
  components: { DefautChamp },
  data() {
    return {
      formReady: false,
      COLONNES_CABLE,
      // sections repliées par défaut (12/10/2026) : réglages rarement modifiés
      replie: { plage: true, calcul: true, couts: true, cable: true, opex: true },
      catalogue: null,        // catalogue du matériel (GET /wizard/catalogue-materiel)
      catalogueErreur: '',
      form: {
        inflationElec: 3.5,
        capaciteUtilisableBatterie: 90,
        hausseMaintenancePct: 2,
        hausseMaintenancePeriode: 5,
        inflationAssurance: 1.5,
        degradationPanneaux: 0.4,
        degradationBatterie: 2,
        rendementBatterie: 90,
        rendementConversionBatterie: 98,
        coutInstallationPV: [],
        coutInstallationBatterie: [],
        rachatSurplusUnitaire: 0.011,
        zeroInjection: true,
        coutRaccordementEnedis: 0,
        travauxSupplementaires: 0,
        travauxSupplementairesLibelle: '',
        prixDatalogger: null,  // € HT saisi (13/10/2026) ; null = prix selon la puissance d'abonnement
        longueurCable: null,   // m (19/10/2026) ; null = câble non chiffré
        erp: false,
        chiffrageMateriel: true, // 03/11/2026 : toujours chiffrée au matériel (une étude d'avant garde ses grilles)
        materiel: { ...DEFAULT_MATERIEL, bornes: [] },
        prixCable: [],
        prixRaccordsAlu: [],
        avecMaintenance: true,
        avecAssurance: true,
        coutMaintenance: [],
        coutAssurance: [],
        avecFinancement: false,
        apportPct: 20,
        tauxEmprunt: 4.5,
        dureeEmprunt: 10,
        dureeEmpruntAuPlusCourt: false,
        pvRangeMin: 1,
        pvRangeMax: null,
        batRangeMin: 5,
        batRangeMax: 25,
      },
    }
  },
  computed: {
    // valeurs par défaut d'une nouvelle étude (store), plage PV déduite de la puissance installable comme au montage
    defauts() {
      // valeurs par défaut du catalogue (30/10/2026), sinon celles de RETIX
      const d = Object.assign(buildInitialState(), defautsEtude())
      const pvMax = Math.round(this.puissanceTotaleKwc || 0) || null
      d.pvRangeMax = pvMax
      d.pvRangeMin = pvMax ? Math.max(this.pvMinimum, Math.round(pvMax / 5)) : this.pvMinimum
      return d
    },
    nbModifies() {
      const n = {}
      Object.keys(SECTIONS).forEach(section => {
        n[section] = SECTIONS[section].reduce((total, cle) => {
          const v = this.form[cle]
          if (cle === 'prixCable') return total + v.reduce((t, row, idx) => t + COLONNES_CABLE.filter(col => estModifie(row[col], this.prixCableDefaut(idx, col))).length, 0)
          if (Array.isArray(v)) return total + v.filter((row, idx) => estModifie(row.prix, this.prixDefaut(cle, idx))).length
          return total + (estModifie(v, this.defauts[cle]) ? 1 : 0)
        }, 0)
      })
      return n
    },
    resumePlage() {
      const f = this.form
      return 'PV ' + (f.pvRangeMin || '—') + ' à ' + (f.pvRangeMax || '—') + ' kWc · batterie ' + (f.batRangeMin != null ? f.batRangeMin : '—') + ' à ' + (f.batRangeMax || '—') + ' kWh'
    },
    resumeCalcul() {
      const f = this.form
      const n = v => String(v).replace('.', ',')
      return 'Inflation ' + n(f.inflationElec) + ' %/an · dégradation PV ' + n(f.degradationPanneaux) + ' %/an · batterie utilisée à ' + f.capaciteUtilisableBatterie + ' %'
    },
    // B41 : une plage min > max ne doit pas partir au calcul
    plagePvIncoherente() {
      const f = this.form
      return f.pvRangeMin != null && f.pvRangeMax != null && f.pvRangeMin !== '' && f.pvRangeMax !== '' && f.pvRangeMin > f.pvRangeMax
    },
    plageBatIncoherente() {
      const f = this.form
      return f.batRangeMin != null && f.batRangeMax != null && f.batRangeMin !== '' && f.batRangeMax !== '' && f.batRangeMin > f.batRangeMax
    },
    plagesIncoherentes() { return this.plagePvIncoherente || this.plageBatIncoherente || this.plagePvSousMinimumPro || this.toiturePropTropPetite },
    estPro() { return this.clientType === 'Professionnel' },
    // limites de puissance du catalogue (30/10/2026)
    kwcMinPro() { const c = catalogueEtude.choix; return c && c.limites ? c.limites.kwcMinPro : KWC_MIN_PRO },
    kwcMaxParticulier() { const c = catalogueEtude.choix; return c && c.limites ? c.limites.kwcMaxParticulier : 15 },
    pvMinimum() { return this.estPro ? this.kwcMinPro : 1 },
    plagePvSousMinimumPro() {
      const v = this.form.pvRangeMin
      return this.estPro && v != null && v !== '' && v < this.kwcMinPro
    },
    toiturePropTropPetite() {
      return this.estPro && this.puissanceTotaleKwc > 0 && this.puissanceTotaleKwc < this.kwcMinPro
    },
    // interrupteur « Vente de surplus » : désactivé = zéro-injection (13/10/2026)
    venteSurplus: {
      get() { return !this.form.zeroInjection },
      set(v) { this.form.zeroInjection = !v },
    },
    // même règle que le serveur (WizardService.prixDatalogger) : selon la puissance d'abonnement (13/10/2026), prix et
    // seuil du catalogue (30/10/2026)
    prixDataloggerDefaut() {
      const c = catalogueEtude.choix
      const d = c && c.datalogger ? c.datalogger : { prixJusquAuSeuil: 1000, prixAuDela: 1500, seuilKva: 36 }
      return this.puissanceAbonnement != null && this.puissanceAbonnement > d.seuilKva ? d.prixAuDela : d.prixJusquAuSeuil
    },
    ...mapState('wizard', [
      'projectName', 'inflationElec', 'capaciteUtilisableBatterie', 'hausseMaintenancePct', 'hausseMaintenancePeriode', 'inflationAssurance',
      'degradationPanneaux', 'degradationBatterie', 'rendementBatterie', 'rendementConversionBatterie',
      'coutInstallationPV', 'coutInstallationBatterie',
      'rachatSurplusUnitaire', 'zeroInjection', 'coutRaccordementEnedis', 'travauxSupplementaires', 'travauxSupplementairesLibelle', 'prixDatalogger', 'avecMaintenance', 'avecAssurance', 'coutMaintenance', 'coutAssurance',
      'avecFinancement', 'apportPct', 'tauxEmprunt', 'dureeEmprunt', 'dureeEmpruntAuPlusCourt',
      'pvRangeMin', 'pvRangeMax', 'batRangeMin', 'batRangeMax', 'puissanceTotaleKwc',
      'clientType', 'puissanceAbonnement', 'longueurCable', 'erp', 'prixCable', 'prixRaccordsAlu', 'chiffrageMateriel', 'materiel', 'panneauCatalogue',
      'modeleProduction', 'toitures', 'typologie', 'integrationToiture',
    ]),
    estParticulier() { return this.clientType === 'Particulier' },
    // ratios panneaux / onduleur saisis par le commercial (vide = valeur du catalogue) ; $set : anciennes études sans ces champs
    ratioReseau: {
      get() { return this.form.materiel.ratioReseau },
      set(v) { this.$set(this.form.materiel, 'ratioReseau', v === '' ? null : v) },
    },
    ratioHybride: {
      get() { return this.form.materiel.ratioHybride },
      set(v) { this.$set(this.form.materiel, 'ratioHybride', v === '' ? null : v) },
    },
    // marges des sections (03/11/2026) : minimum du catalogue pour le type de client et la puissance maximale étudiée
    margesAffichees() {
      const min = margesMinimales({ clientType: this.clientType, materiel: this.form.materiel }, this.form.pvRangeMax) || {}
      return SECTIONS_MARGE.filter(m => (m.section !== 'PRESTA' || !this.estParticulier) && (m.section !== 'BORNES' || this.form.materiel.bornes.length))
        .map(m => {
          const v = this.form.materiel[m.cle]
          return { ...m, min: min[m.catalogue], sousMinimum: v != null && v !== '' && min[m.catalogue] != null && v < min[m.catalogue] }
        })
    },
    bornesCatalogue() { return (this.catalogueClient && this.catalogueClient.bornes) || [] },
    toituresEtude() {
      if (this.modeleProduction === 'pvgis') return this.toitures || []
      return [{ typologie: this.typologie, integration: this.integrationToiture }]
    },
    aDesOmbrieres() { return this.toituresEtude.some(t => t.typologie === 'ombriere') },
    integrationsTexte() {
      const t = this.toituresEtude
      if (!t.length) return ''
      const lib = v => (TYPOLOGIES.find(x => x.valeur === v) || {}).libelle || 'Toiture inclinée'
      return t.map((x, i) => (t.length > 1 ? 'Toiture ' + (i + 1) + ' : ' : '') + lib(x.typologie) + ' — '
        + (x.integration || (x.typologie === 'ombriere' ? 'structure en prestation externe' : 'aucun système'))).join(' ; ') + ' (choisis à l\'étape 2)'
    },
    batterieAChoisir() { return !this.estParticulier && (this.form.batRangeMax || 0) > 0 && !this.systemeBatterie },
    /** Choix obligatoires du matériel (03/11/2026) : fabricant des onduleurs réseau, gamme d'hybrides et de batteries (pro). */
    materielIncomplet() {
      if (!this.form.chiffrageMateriel || !this.catalogueClient || this.estParticulier) return ''
      if (!this.form.materiel.marqueOnduleur) return 'Choisissez le fabricant des onduleurs réseau (Paramétrage des coûts).'
      if (this.batterieAChoisir) return 'Choisissez la gamme d\'onduleurs hybrides et de batteries (Paramétrage des coûts).'
      return ''
    },
    ratioReseauEffectif() {
      const v = this.form.materiel.ratioReseau
      return v > 0 ? v : (this.catalogue && this.catalogue.ratios ? this.catalogue.ratios.reseau : 0)
    },
    ratioHybrideEffectif() {
      const v = this.form.materiel.ratioHybride
      return v > 0 ? v : (this.catalogue && this.catalogue.ratios ? this.catalogue.ratios.hybride : 0)
    },
    catalogueClient() {
      if (!this.catalogue) return null
      return this.estParticulier ? this.catalogue.particulier : this.catalogue.pro
    },
    batteriesTexte() {
      if (!this.catalogueClient) return ''
      if (this.estParticulier) {
        const kwh = (this.catalogueClient.batteriesParMarque || {})[''] || []
        return kwh.join(', ') + ' kWh (dans la plage d\'analyse)'
      }
      const choix = this.systemeBatterie
      if (!choix) return 'toutes celles du catalogue, la plus rentable pour chaque capacité (dans la plage d\'analyse)'
      const o = (this.catalogueClient.systemesBatterie || []).find(x => x.valeur === choix)
      if (!o || !o.kwh.length) return 'aucune pour ce choix (pas de colonne batterie)'
      return this.libelleSystemeBatterie(o) + ', la moins chère pour chaque capacité (dans la plage d\'analyse)'
    },
    systemeBatterie: {
      get() { return this.form.materiel.systemeBatterie || '' },
      set(v) { this.$set(this.form.materiel, 'systemeBatterie', v) },
    },

    // professionnel chiffré au matériel (28/10/2026) : câbles du catalogue à la place de la grille
    cablesCatalogue() { return !!this.form.chiffrageMateriel && !this.estParticulier },
    marquesOnduleurs() {
      if (!this.catalogueClient) return []
      return [...new Set((this.catalogueClient.gammes || []).map(g => g.marque))]
    },
    // panneau choisi en Step 2 (27/10/2026)
    panneauTexte() {
      if (!this.catalogueClient) return ''
      const liste = this.catalogueClient.panneaux || []
      if (this.panneauCatalogue === '*') return 'le plus rentable (étude d\'avant le 03/11/2026) — choisissez un panneau à l\'étape 2'
      const p = liste.find(x => x.ref === this.panneauCatalogue)
      if (p) return p.libelle + ' (' + p.wc + ' Wc' + (this.estParticulier ? ', TVA ' + (p.tva55 ? '5,5' : '20') + ' %' : '') + ') — choisi en Step 2'
      return liste.length ? 'aucun choisi en Step 2 : ' + liste[0].libelle + ' par défaut' : ''
    },
    ...mapGetters('wizard', ['nomRapportDefault']),
  },
  watch: {
    form: {
      deep: true,
      handler() { if (this.formReady) this.SET_DIRTY(true) },
    },
  },
  mounted() {
    Object.assign(this.form, {
      inflationElec: this.inflationElec,
      capaciteUtilisableBatterie: this.capaciteUtilisableBatterie != null ? this.capaciteUtilisableBatterie : 90,
      hausseMaintenancePct: this.hausseMaintenancePct,
      hausseMaintenancePeriode: this.hausseMaintenancePeriode,
      inflationAssurance: this.inflationAssurance,
      degradationPanneaux: this.degradationPanneaux,
      degradationBatterie: this.degradationBatterie,
      rendementBatterie: this.rendementBatterie,
      rendementConversionBatterie: this.rendementConversionBatterie,
      coutInstallationPV: JSON.parse(JSON.stringify(this.coutInstallationPV)),
      coutInstallationBatterie: JSON.parse(JSON.stringify(this.coutInstallationBatterie)),
      rachatSurplusUnitaire: this.rachatSurplusUnitaire,
      zeroInjection: this.zeroInjection,
      coutRaccordementEnedis: this.coutRaccordementEnedis,
      travauxSupplementaires: this.travauxSupplementaires || 0,
      travauxSupplementairesLibelle: this.travauxSupplementairesLibelle || '',
      prixDatalogger: this.prixDatalogger != null ? this.prixDatalogger : null,
      longueurCable: this.longueurCable != null ? this.longueurCable : null,
      erp: !!this.erp,
      chiffrageMateriel: !!this.chiffrageMateriel,
      materiel: { ...DEFAULT_MATERIEL, ...JSON.parse(JSON.stringify(this.materiel || {})) },
      prixCable: JSON.parse(JSON.stringify(this.prixCable || this.defauts.prixCable)),
      prixRaccordsAlu: JSON.parse(JSON.stringify(this.prixRaccordsAlu || this.defauts.prixRaccordsAlu)),
      avecMaintenance: this.avecMaintenance,
      avecAssurance: this.avecAssurance !== false,
      coutMaintenance: JSON.parse(JSON.stringify(this.coutMaintenance)),
      coutAssurance: JSON.parse(JSON.stringify(this.coutAssurance)),
      avecFinancement: this.avecFinancement,
      apportPct: this.apportPct,
      tauxEmprunt: this.tauxEmprunt,
      dureeEmprunt: this.dureeEmprunt,
      dureeEmpruntAuPlusCourt: !!this.dureeEmpruntAuPlusCourt,
      batRangeMin: this.batRangeMin,
      batRangeMax: this.batRangeMax,
    })
    // pvRangeMax déduit de la puissance installée si non encore défini
    var pvMax = this.pvRangeMax || Math.round(this.puissanceTotaleKwc || 0) || null
    this.form.pvRangeMax = pvMax
    this.form.pvRangeMin = this.pvRangeMin !== null
      ? this.pvRangeMin
      : (pvMax ? Math.max(this.pvMinimum, Math.round(pvMax / 5)) : this.pvMinimum)
    // étude professionnelle enregistrée avant le 27/10/2026 avec un minimum sous 9 kWc : ramené à 9 kWc
    if (this.estPro && this.form.pvRangeMin !== '' && this.form.pvRangeMin != null && this.form.pvRangeMin < this.kwcMinPro) this.form.pvRangeMin = this.kwcMinPro
    // plage à corriger : cadre déplié pour voir le message
    if (this.plagesIncoherentes) this.replie.plage = false
    this.$nextTick(() => { this.formReady = true })
    this.chargerCatalogue()
  },
  beforeDestroy() {
    this.SET_STEP4({
      inflationElec: this.form.inflationElec,
        capaciteUtilisableBatterie: this.form.capaciteUtilisableBatterie,
      hausseMaintenancePct: this.form.hausseMaintenancePct,
      hausseMaintenancePeriode: this.form.hausseMaintenancePeriode,
      inflationAssurance: this.form.inflationAssurance,
      degradationPanneaux: this.form.degradationPanneaux,
      degradationBatterie: this.form.degradationBatterie,
      rendementBatterie: this.form.rendementBatterie,
      rendementConversionBatterie: this.form.rendementConversionBatterie,
      coutInstallationPV: JSON.parse(JSON.stringify(this.form.coutInstallationPV)),
      coutInstallationBatterie: JSON.parse(JSON.stringify(this.form.coutInstallationBatterie)),
      rachatSurplusUnitaire: this.form.rachatSurplusUnitaire,
      zeroInjection: this.form.zeroInjection,
      coutRaccordementEnedis: this.form.coutRaccordementEnedis,
      travauxSupplementaires: this.form.travauxSupplementaires || 0,
      travauxSupplementairesLibelle: this.form.travauxSupplementairesLibelle || '',
        prixDatalogger: this.form.prixDatalogger,
        longueurCable: this.form.longueurCable,
        erp: this.form.erp,
        chiffrageMateriel: this.form.chiffrageMateriel,
        materiel: JSON.parse(JSON.stringify(this.form.materiel)),
        prixCable: JSON.parse(JSON.stringify(this.form.prixCable)),
        prixRaccordsAlu: JSON.parse(JSON.stringify(this.form.prixRaccordsAlu)),
      avecMaintenance: this.form.avecMaintenance,
      avecAssurance: this.form.avecAssurance,
      coutMaintenance: JSON.parse(JSON.stringify(this.form.coutMaintenance)),
      coutAssurance: JSON.parse(JSON.stringify(this.form.coutAssurance)),
      avecFinancement: this.form.avecFinancement,
      apportPct: this.form.apportPct,
      tauxEmprunt: this.form.tauxEmprunt,
      dureeEmprunt: this.form.dureeEmprunt,
      dureeEmpruntAuPlusCourt: this.form.dureeEmpruntAuPlusCourt,
      pvRangeMin: this.form.pvRangeMin,
      pvRangeMax: this.form.pvRangeMax,
      batRangeMin: this.form.batRangeMin,
      batRangeMax: this.form.batRangeMax,
    })
  },
  methods: {
    nombreFr,
    // « 16,5 à 137,5 kVA » (02/11/2026)
    plageKva(g) {
      const n = v => nombreFr(v, Number.isInteger(v) ? 0 : 1)
      return g.kvaMin === g.kvaMax ? n(g.kvaMin) + ' kVA' : n(g.kvaMin) + ' à ' + n(g.kvaMax) + ' kVA'
    },
    // « FOX ESS : onduleurs hybrides 16,5 à 137,5 kVA » ; kit : « Kit FOX ESS : 55 à 137,5 kVA, 241 kWh »
    libelleSystemeBatterie(o) {
      if (o.kit) return 'Kit ' + o.marque + ' : ' + this.plageKva(o) + (o.kwh.length ? ', ' + nombreFr(o.kwh[0], 0) + ' kWh' : '')
      return o.marque + ' : onduleurs hybrides ' + this.plageKva(o)
    },
    ...mapMutations('wizard', ['SET_DIRTY', 'SET_STEP4']),
    changerMarge(cle, v) { this.$set(this.form.materiel, cle, v === '' || v === null ? null : parseFloat(v)) },
    async chargerCatalogue() {
      this.catalogue = await chargerCatalogueEtude()
      if (!this.catalogue) this.catalogueErreur = 'Catalogue du matériel indisponible : le calcul utilisera quand même le catalogue du serveur.'
    },
    retablir(cle) { this.form[cle] = this.defauts[cle] },
    // prix par défaut d'une ligne de grille (même libellé de tranche), divisé par « echelle » pour l'affichage (€/Wc)
    prixDefaut(grille, idx, echelle) {
      const row = this.form[grille][idx]
      const defaut = (this.defauts[grille] || []).find(t => t.label === row.label)
      if (!defaut) return null
      return echelle ? defaut.prix / echelle : defaut.prix
    },
    retablirTranche(grille, idx) {
      const p = this.prixDefaut(grille, idx)
      if (p != null) this.form[grille][idx].prix = p
    },
    // prix par défaut d'une case de la grille du câble (même section)
    prixCableDefaut(idx, col) {
      const row = this.form.prixCable[idx]
      const defaut = (this.defauts.prixCable || []).find(t => t.section === row.section)
      return defaut ? defaut[col] : null
    },
    sectionTexte(v) { return String(v).replace('.', ',') },
    libelleModifies(n) { return n + (n > 1 ? ' réglages modifiés' : ' réglage modifié') },
    submit() {
      if (this.plagesIncoherentes || this.materielIncomplet) return
      this.SET_STEP4({
        inflationElec: this.form.inflationElec,
        capaciteUtilisableBatterie: this.form.capaciteUtilisableBatterie,
        hausseMaintenancePct: this.form.hausseMaintenancePct,
      hausseMaintenancePeriode: this.form.hausseMaintenancePeriode,
      inflationAssurance: this.form.inflationAssurance,
        degradationPanneaux: this.form.degradationPanneaux,
        degradationBatterie: this.form.degradationBatterie,
        rendementBatterie: this.form.rendementBatterie,
        rendementConversionBatterie: this.form.rendementConversionBatterie,
        coutInstallationPV: JSON.parse(JSON.stringify(this.form.coutInstallationPV)),
        coutInstallationBatterie: JSON.parse(JSON.stringify(this.form.coutInstallationBatterie)),
        rachatSurplusUnitaire: this.form.rachatSurplusUnitaire,
        zeroInjection: this.form.zeroInjection,
        coutRaccordementEnedis: this.form.coutRaccordementEnedis || 0,
        travauxSupplementaires: this.form.travauxSupplementaires || 0,
        travauxSupplementairesLibelle: this.form.travauxSupplementairesLibelle || '',
        prixDatalogger: this.form.prixDatalogger,
        longueurCable: this.form.longueurCable,
        erp: this.form.erp,
        chiffrageMateriel: this.form.chiffrageMateriel,
        materiel: JSON.parse(JSON.stringify(this.form.materiel)),
        prixCable: JSON.parse(JSON.stringify(this.form.prixCable)),
        prixRaccordsAlu: JSON.parse(JSON.stringify(this.form.prixRaccordsAlu)),
        avecMaintenance: this.form.avecMaintenance,
        avecAssurance: this.form.avecAssurance,
        coutMaintenance: JSON.parse(JSON.stringify(this.form.coutMaintenance)),
        coutAssurance: JSON.parse(JSON.stringify(this.form.coutAssurance)),
        avecFinancement: this.form.avecFinancement,
        apportPct: this.form.apportPct,
        tauxEmprunt: this.form.tauxEmprunt,
        dureeEmprunt: this.form.dureeEmprunt,
        dureeEmpruntAuPlusCourt: this.form.dureeEmpruntAuPlusCourt,
        pvRangeMin: this.form.pvRangeMin || 1,
        pvRangeMax: this.form.pvRangeMax || Math.round(this.puissanceTotaleKwc || 0) || 1,
        batRangeMin: this.form.batRangeMin !== null && this.form.batRangeMin !== undefined ? this.form.batRangeMin : 5,
        batRangeMax: this.form.batRangeMax || 25,
      })
      this.$emit('next')
    },
  },
}
</script>

<style scoped>
/* câble onduleur ↔ TGBT (19/10/2026) */
.params-table--cable th, .params-table--cable td { padding-right: 24px; }
.params-table--cable .form-input--sm { width: 80px; }
.params-table--raccords { margin-top: 12px; }

/* réglage modifié : case surlignée, valeur par défaut affichée à côté (19/10/2026) ; pas dans « Paramétrage des coûts » (choix de chaque étude) */
.form-group:has(.defaut-champ) > .form-input, td:has(.defaut-champ) .form-input { background: #fff8e6; border-color: #e0c27a; }
.form-group .defaut-champ { display: flex; }
td .defaut-champ { display: flex; }
.opex-phrase .defaut-champ { margin-left: 8px; }
.badge-modifies { margin-left: 10px; padding: 1px 8px; border-radius: 10px; background: #fff3d6; color: #8a5a00; font-size: 12px; font-weight: 600; }

.step-page {
  font-family: 'Raleway', sans-serif;
  background: #f5f5f5;
  min-height: calc(100vh - 140px);
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.step-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px; }

.step-badge {
  display: inline-block;
  background: #f5c518;
  color: #222;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.step-content { display: flex; flex-direction: column; gap: 20px; }

.card-block {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-block__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
}

.card-block__title {
  font-size: 16px;
  font-weight: 700;
  color: #222;
  display: inline-block;
}

/* titre seul (sans .card-block__header) : garde sa bordure basse */
.card-block > .card-block__title {
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-row__label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
}

.toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.toggle-switch input { opacity: 0; width: 0; height: 0; }

.toggle-slider {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: #ddd; border-radius: 24px; cursor: pointer;
  transition: background 0.2s;
}

.toggle-switch input:checked + .toggle-slider { background: #f5c518; }

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px; width: 18px;
  left: 3px; bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .toggle-slider::before { transform: translateX(20px); }

.financement-off-hint {
  font-size: 13px;
  color: #aaa;
  font-style: italic;
  margin: 0;
  padding: 8px 0;
}

.card-subtitle {
  font-size: 13px;
  color: #888;
  margin-bottom: 20px;
  margin-top: -10px;
}

.form-grid {
  display: grid;
  gap: 16px;
}

.form-grid--3 { grid-template-columns: repeat(3, 1fr); }
.form-grid--4 { grid-template-columns: repeat(4, 1fr); }

.form-group { display: flex; flex-direction: column; gap: 4px; }

.form-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.form-input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  color: #222;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-input:focus { border-color: #f5c518; box-shadow: 0 0 0 2px rgba(245,197,24,0.2); }

.form-input--sm { padding: 6px 10px; font-size: 13px; }

/* Tables */
/* tableaux à la largeur de leur contenu, côte à côte (19/10/2026 : les prix étaient loin des libellés) */
.two-tables {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px 64px;
}

.table-block__title {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}

.table-block__subtitle {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  margin-top: 20px;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e0e0e0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.enedis-field {
  display: flex;
  align-items: center;
  gap: 16px;
}

.params-table {
  width: auto;
  border-collapse: collapse;
}

.params-table th {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-align: left;
  padding: 6px 8px;
  border-bottom: 2px solid #f0f0f0;
}

.params-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
}

.label-cell {
  padding-right: 32px !important;
  font-size: 13px;
  color: #444;
  font-weight: 600;
}

.input-unit {
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-unit .form-input { max-width: 100px; }

.unit {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.field-note {
  font-size: 12px;
  color: #6b7280; /* plus lisible (13/10/2026) */
  margin-top: 6px;
  line-height: 1.5;
}





.range-group {
  margin-bottom: 20px;
}

.range-group:last-child { margin-bottom: 0; }

.range-group__label {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  margin-bottom: 10px;
  padding-left: 2px;
}

/* Info banner */
.info-banner {
  background: #fffde7;
  border: 1px solid #f5c518;
  border-radius: 10px;
  padding: 14px 20px;
  font-size: 14px;
  color: #444;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-banner__icon { font-size: 18px; flex-shrink: 0; }

/* Footer */
.step-footer { display: flex; gap: 12px; padding-top: 8px; justify-content: center; }

.btn-back {
  padding: 12px 32px;
  border: 1px solid #ccc;
  border-radius: 24px;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
}

.btn-back:hover { border-color: #999; }

.btn-next {
  padding: 12px 40px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-next:hover { background: #e6b800; }
.btn-next:disabled { opacity: 0.5; cursor: not-allowed; }
.travaux-libelle { margin-top: 6px; }
.range-error { color: #c62828; font-size: 13px; margin: 6px 0 0; }
.footer-erreur { margin: 0 12px 0 auto; align-self: center; }
.opex-phrases { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.opex-phrase { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 0; font-size: 14px; }
.opex-phrase label { margin: 0; font-weight: 600; }
.form-input.trou { width: 80px; text-align: center; padding: 4px 8px; }
.sous-sous-titre { font-size: 12px; font-weight: 700; color: #1a1a2e; margin: 14px 0 6px; padding-bottom: 4px; border-bottom: 1px dashed #e3e3e3; }
.sous-titre-note { font-weight: 400; color: #777; }
.champ-a-choisir { border-color: #e65100 !important; background: #fff8e1; }
.materiel-select--court { width: 180px !important; }
.choix-ombrieres { display: flex; gap: 18px; flex-wrap: wrap; }
.cout-ligne--haut { align-items: flex-start; }
.bornes { display: flex; flex-direction: column; gap: 6px; }
.borne-ligne { display: flex; gap: 8px; align-items: center; }
.borne-nombre { width: 70px; }
.btn-retirer-borne { border: none; background: none; color: #c62828; cursor: pointer; font-size: 14px; }
.btn-ajouter-borne, .btn-passer-materiel { align-self: flex-start; border: 1px solid #1a1a2e; background: #fff; border-radius: 16px; padding: 4px 12px; font-size: 13px; cursor: pointer; }
.marge-sous-minimum { margin-left: 8px; }
.materiel-integrations { max-width: 640px; }
.badge-provisoire {
  font-size: 11px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 4px;
}
.materiel-choix {
  border-left: 3px solid #f2b705;
  padding: 4px 0 4px 14px;
  margin: 0 0 12px;
}
.cout-ligne .materiel-select { width: 360px; max-width: 100%; }
.materiel-case { display: flex; gap: 8px; align-items: center; font-size: 13px; cursor: pointer; }
.materiel-panneau, .materiel-systeme { font-size: 14px; color: #3a3f4b; }
.materiel-note { font-size: 12px; color: #555; margin: 6px 0; }
.materiel-erreur { font-size: 12px; color: #c62828; }
.materiel-hypotheses { font-size: 12px; color: #555; }
.materiel-hypotheses summary { cursor: pointer; font-weight: 600; }
.materiel-hypotheses ul { margin: 6px 0 0; padding-left: 18px; }
.grilles-inutilisees { font-size: 12px; font-weight: 600; color: #b45309; margin: 0 0 10px; }
.two-tables--inutilisees { opacity: 0.45; }

.section-repliable {
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px 10px; width: 100%;
  background: none; border: 0; border-bottom: 2px solid #f5c518; padding: 0 0 10px; margin-bottom: 0;
  text-align: left; cursor: pointer; font: inherit; font-size: 16px; font-weight: 700; color: #222;
}
.card-block > .section-repliable { display: flex; margin-bottom: 18px; }
.card-block > .section-repliable[aria-expanded="false"] { margin-bottom: 0; border-bottom-color: transparent; padding-bottom: 0; }
.section-repliable:focus-visible { outline: 2px solid #f5c518; outline-offset: 4px; }
.section-repliable__chevron { width: 14px; color: #b08d00; }
.section-repliable__resume { font-size: 13px; font-weight: 500; color: #888; }
.cout-ligne { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 16px; min-height: 38px; margin-bottom: 10px; }
.cout-ligne__libelle { width: 260px; margin: 0; font-size: 14px; font-weight: 600; color: #444; }
.cout-ligne .input-unit { width: 180px; }
.cout-ligne .toggle-row { min-width: 150px; }
.cout-ligne .travaux-libelle { flex: 1; min-width: 220px; margin: 0; }
.sous-titre { font-size: 13px; font-weight: 700; color: #555; text-transform: uppercase; letter-spacing: .04em; margin: 4px 0 12px; }
.parametrage-couts .sous-titre ~ .sous-titre { margin-top: 18px; padding-top: 16px; border-top: 1px solid #eee; }
.opex-off { margin: 0; font-size: 13px; color: #999; }
</style>
